import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, interval } from 'rxjs';
import { distinctUntilChanged, filter, map, withLatestFrom } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { DropDownList } from '@core/interfaces';
import { EventService, ListService, fromList } from '@core/services';

import { endpoints } from '@environment';

import {
  ReportCompletedRequest,
  ReportCompletedResponseBody,
  ReportDownloadRequest,
  ReportDownloadResponseBody,
  ReportGenerateRequest,
  ReportGenerateResponseBody,
  ReportGetParamsRequest,
  ReportGetParamsResponseBody,
  ReportLookupRequest,
  ReportLookupResponseBody,
  ReportRefreshRequest,
  ReportRefreshResponseBody,
  ReportSetParamsRequest,
  ReportSetParamsResponseBody,
  ReportStatusRequest,
  ReportStatusResponseBody,
} from '#types/report';

import { AuthService } from '@auth/services';
import { CONSTANTS } from '@report/constants';
import { fromReport } from '@report/state';
import { ReportActions } from '@report/state';

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  reportLoading$ = new BehaviorSubject<boolean>(false);

  requestId$ = this.store.select(fromReport.selectRequestId);
  reportName$ = this.store.select(fromReport.selectReportName);
  format$ = this.store.select(fromReport.selectFormat);
  parameters$ = this.store.select(fromReport.selectParameters);
  filename$ = this.store.select(fromReport.selectFilename);
  hasNoParameters$ = this.store.select(fromReport.selectHasNoParameters);

  pendingRequests$ = this.store.select(fromReport.selectPendingRequests);

  folderList$ = this.store.select(fromList.selectReportFolderList);
  folderDropdownList$ = new BehaviorSubject<DropDownList>(null);

  formatList$ = this.store.select(fromList.selectReportFormatList);
  formatDropdownList$ = new BehaviorSubject<DropDownList>(null);

  reportsList$ = this.store.select(fromList.selectReportReportsList);
  reportsDropdownList$ = new BehaviorSubject<DropDownList>(null);

  displayRunReportDialog$ = new BehaviorSubject<boolean>(false);
  lookupGridPanelHeight$ = new BehaviorSubject<number>(0);

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private authService: AuthService,
    private listService: ListService,
    private http: HttpClient,
    private store: Store,
    private translocoService: TranslocoService,
    private eventService: EventService
  ) {
    this.translocoService
      .selectTranslation()
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        this.authService.authenticated$.pipe(filter((authenticated) => authenticated)).subscribe(() => {
          this.initializeDropdownLists();
        });
      });

    // check status for any pending reports every 5 seconds
    interval(5000)
      .pipe(
        withLatestFrom(this.pendingRequests$),
        filter(([, x]) => !!x && x.length > 0)
      )
      .subscribe(([, requests]) => {
        this.store.dispatch(ReportActions.RequestReportStatus({ pendingRequests: requests.map((x) => x.requestId) }));
      });
  }

  requestLists() {
    this.store.dispatch(ReportActions.RequestLists());
  }

  reportLookup(payload: ReportLookupRequest) {
    return this.http.post(endpoints.report.lookup, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.GRID_REPORT_LOOKUP),
      map((response: ReportLookupResponseBody) => response)
    );
  }

  reportCompleted(payload: ReportCompletedRequest) {
    return this.http.post(endpoints.report.completed, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.GRID_REPORT_COMPLETED),
      map((response: ReportCompletedResponseBody) => response)
    );
  }

  reportRefresh(payload: ReportRefreshRequest) {
    return this.http.post(endpoints.report.refresh, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_REFRESH),
      map((response: ReportRefreshResponseBody) => response)
    );
  }

  reportStatus(payload: ReportStatusRequest) {
    return this.http.post(endpoints.report.status, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_STATUS),
      map((response: ReportStatusResponseBody) => response)
    );
  }

  reportGetParams(payload: ReportGetParamsRequest) {
    return this.http.post(endpoints.report.getParams, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_GET_PARAMS),
      map((response: ReportGetParamsResponseBody) => response)
    );
  }

  reportSetParams(payload: ReportSetParamsRequest) {
    return this.http.post(endpoints.report.setParams, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_SET_PARAMS),
      map((response: ReportSetParamsResponseBody) => response)
    );
  }

  reportGenerate(payload: ReportGenerateRequest) {
    return this.http.post(endpoints.report.generate, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_GENERATE),
      map((response: ReportGenerateResponseBody) => response)
    );
  }

  reportDownload(payload: ReportDownloadRequest) {
    return this.http.post(endpoints.report.download, payload, { withCredentials: true, responseType: 'blob' }).pipe(
      this.eventService.indicate(CONSTANTS.REQUEST_DOWNLOAD),
      map((response: ReportDownloadResponseBody) => response)
    );
  }

  initializeDropdownLists() {
    this.folderList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.folderDropdownList$.next(
        this.listService.getDropdownList(list, {
          includeHeader: false,
          header1: this.translate('core.services.list.dropdown.header.code'),
          header2: this.translate('core.services.list.dropdown.header.description'),
        })
      );
    });

    this.formatList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.formatDropdownList$.next(
        this.listService.getDropdownList(list, {
          includeHeader: false,
          header1: this.translate('core.services.list.dropdown.header.code'),
          header2: this.translate('core.services.list.dropdown.header.description'),
        })
      );
    });

    this.reportsList$.pipe(distinctUntilChanged()).subscribe((list) => {
      this.reportsDropdownList$.next(
        this.listService.getDropdownList(list, {
          includeHeader: false,
          header1: this.translate('core.services.list.dropdown.header.code'),
          header2: this.translate('core.services.list.dropdown.header.description'),
        })
      );
    });
  }

  download(filename: string, mimeType: string, data: any) {
    const blob = new Blob([data], {
      type: mimeType,
    });

    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;

    a.download = filename;

    document.body.appendChild(a);
    a.style.display = 'none';
    a.click();
    a.remove();

    setTimeout(() => {
      this.reportLoading$.next(false);
      this.displayRunReportDialog$.next(false);
      window.URL.revokeObjectURL(url);
    }, 1000);
  }
}
